import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  List,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { chatAPI, ChatReview, ChatReviewCreate, ChatReviewUpdate } from '../../services/api';

interface ReviewComponentProps {
  sessionId: string;
  onReviewAdded?: () => void;
  showAddButton?: boolean;
  isPublicView?: boolean;
  initialReviews?: any[]; // For shared views
}

const ReviewComponent: React.FC<ReviewComponentProps> = ({
  sessionId,
  onReviewAdded,
  showAddButton = true,
  isPublicView = false,
  initialReviews = [],
}) => {
  const [reviews, setReviews] = useState<ChatReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState<ChatReview | null>(null);
  const loadingRef = useRef(false);
  
  // Simplified form state - just content and type for session notes
  const [reviewType, setReviewType] = useState<'general' | 'problem' | 'suggestion' | 'knowledgebase'>('general');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reviewTypeLabels = {
    general: 'General',
    problem: 'Problem',
    suggestion: 'Suggestion',
    knowledgebase: 'Knowledgebase',
  };

  const loadReviews = useCallback(async () => {
    if (isPublicView || loadingRef.current || !sessionId) return; // Don't reload in public view, if already loading, or without sessionId
    
    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      
      const response = await chatAPI.getSessionReviews(sessionId);
      setReviews(response.reviews);
    } catch (err: any) {
      console.error('Error loading reviews:', err);
      setError('Failed to load notes');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [sessionId, isPublicView]);

  useEffect(() => {
    if (!isPublicView && sessionId && !loadingRef.current) {
      // Only load reviews if not in public view and we have a sessionId
      loadReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isPublicView]);

  // Separate useEffect for handling initial reviews in public view
  useEffect(() => {
    if (isPublicView && initialReviews) {
      setReviews(initialReviews);
    }
  }, [isPublicView, initialReviews]);

  const handleAddReview = () => {
    resetForm();
    setEditingReview(null);
    setDialogOpen(true);
  };

  const handleEditReview = (review: ChatReview) => {
    setReviewType(review.review_type as any);
    setContent(review.content);
    setEditingReview(review);
    setDialogOpen(true);
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      await chatAPI.deleteReview(reviewId);
      await loadReviews();
    } catch (err: any) {
      console.error('Error deleting review:', err);
      setError('Failed to delete note');
    }
  };

  const resetForm = () => {
    setReviewType('general');
    setContent('');
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError('Note content is required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      if (editingReview) {
        // Update existing review
        const updateData: ChatReviewUpdate = {
          review_type: reviewType,
          content: content.trim(),
        };
        await chatAPI.updateReview(editingReview.id, updateData);
      } else {
        // Create new session-level review
        const reviewData: ChatReviewCreate = {
          session: sessionId,
          review_type: reviewType,
          content: content.trim(),
          is_private: false, // Make session notes visible in shared views
        };
        await chatAPI.createReview(reviewData);
      }

      setDialogOpen(false);
      resetForm();
      await loadReviews();
      
      if (onReviewAdded) {
        onReviewAdded();
      }
    } catch (err: any) {
      console.error('Error saving review:', err);
      setError('Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getReviewTypeColor = (type: string) => {
    const colors = {
      session: 'primary',
      diagnosis: 'success',
      treatment: 'warning',
      general: 'default',
    } as const;
    return colors[type as keyof typeof colors] || 'default';
  };

  return (
    <Box>
      {/* Add Review Button - Only show if not public view */}
      {showAddButton && !isPublicView && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddReview}
          >
            Add Session Note
          </Button>
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Reviews List */}
      {reviews.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Session Notes ({reviews.length})
          </Typography>
          {reviews.length > (isPublicView ? 2 : 3) && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Scroll to see all notes
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ 
        // Use full height in public view (shared chat), responsive height otherwise
        maxHeight: isPublicView ? 'none' : { xs: '50vh', sm: '60vh', md: '70vh' },
        height: isPublicView ? 'auto' : 'auto',
        overflowY: isPublicView ? 'visible' : 'auto',
        pr: isPublicView ? 0 : 1, // No padding in public view since parent handles scroll
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(0, 0, 0, 0.3)',
        },
      }}>
        <List sx={{ p: 0 }}>
          {reviews.map((review) => (
            <Paper key={review.id} sx={{ mb: 2, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip 
                    label={reviewTypeLabels[review.review_type as keyof typeof reviewTypeLabels] || review.review_type}
                    color={getReviewTypeColor(review.review_type)}
                    size="small"
                  />
                  {isPublicView && (
                    <Typography variant="caption" color="text.secondary">
                      by {review.user_username || 'User'}
                    </Typography>
                  )}
                </Box>
                {!isPublicView && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditReview(review)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteReview(review.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              
              <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                {review.content}
              </Typography>
              
              <Typography variant="caption" color="text.secondary">
                Added {formatDate(review.created_at)}
              </Typography>
            </Paper>
          ))}
        </List>
      </Box>

      {reviews.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {isPublicView ? 'No session notes available.' : 'No notes yet. Add the first note for this session!'}
        </Typography>
      )}

      {/* Add/Edit Review Dialog - Only in non-public view */}
      {!isPublicView && (
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {editingReview ? 'Edit Session Note' : 'Add Session Note'}
              <IconButton onClick={() => setDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Review Type */}
              <FormControl fullWidth>
                <InputLabel>Note Type</InputLabel>
                <Select
                  value={reviewType}
                  label="Note Type"
                  onChange={(e) => setReviewType(e.target.value as any)}
                >
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="problem">Problem</MenuItem>
                  <MenuItem value="suggestion">Suggestion</MenuItem>
                  <MenuItem value="knowledgebase">Knowledgebase</MenuItem>
                </Select>
              </FormControl>

              {/* Review Content */}
              <TextField
                label="Session Note"
                multiline
                rows={4}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Add your notes, feedback, or suggestions for this session..."
                fullWidth
                required
                helperText="These notes will be visible when the session is shared"
              />
            </Box>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || !content.trim()}
            >
              {submitting ? 'Saving...' : (editingReview ? 'Update Note' : 'Add Note')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ReviewComponent; 